import {
  BOOLEAN_SELECT_LIST_ITEMS,
  CrudMode,
  DataDisplay,
  Grid,
  GridBreak,
  ValidationErrors,
  formatBoolean,
  formatCurrency,
  formatDate,
  formatSchoolYear,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { DatePicker, Input, Select, YesNoSwitch } from "components";
import {
  LkStudentFafsaDisplayFieldId,
  StudentFafsa,
  StudentFafsaEditResource,
  StudentFafsaViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const StudentFafsaPageBody = (props: {
  crudMode: CrudMode;
  fafsa: StudentFafsa;
  setFafsa: (value: StudentFafsa) => void;
  viewResource: StudentFafsaViewResource;
  editResource: StudentFafsaEditResource;
  validationErrors: ValidationErrors;
}) => {
  const properties = props.viewResource.properties || [];
  const { breakpointsRef, xSmallFieldBreakpoints, smallFieldBreakpoints } = useElementBreakpoints();

  const displayFields = props.viewResource.displayFields || [];
  const dateDisplayField = displayFields.find((x) => x.id === LkStudentFafsaDisplayFieldId.Date);
  const verifiedDisplayField = displayFields.find((x) => x.id === LkStudentFafsaDisplayFieldId.Verified);
  const completedDisplayField = displayFields.find((x) => x.id === LkStudentFafsaDisplayFieldId.Completed);
  const notApplicableDisplayField = displayFields.find((x) => x.id === LkStudentFafsaDisplayFieldId.NotApplicable);
  const amountDisplayField = displayFields.find((x) => x.id === LkStudentFafsaDisplayFieldId.Amount);

  const yearField = () => {
    const propertyName = pn<StudentFafsa>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.fafsa.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.fafsa.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFafsa(
            produce(props.fafsa, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const dateField = () => {
    const propertyName = pn<StudentFafsa>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={dateDisplayField?.displayName}>{formatDate(props.fafsa.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        label={dateDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.fafsa.date}
        onChange={(value) =>
          props.setFafsa(
            produce(props.fafsa, (draft) => {
              draft.date = value;
            })
          )
        }
      />
    );
  };

  const amountField = () => {
    const propertyName = pn<StudentFafsa>("amount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={amountDisplayField?.displayName}>{formatCurrency(props.fafsa.amount)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={amountDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.fafsa.amount}
        onChange={(e) =>
          props.setFafsa(
            produce(props.fafsa, (draft) => {
              draft.amount = e.target.value as unknown as number;
            })
          )
        }
      />
    );
  };

  const submittedField = () => {
    const propertyName = pn<StudentFafsa>("submitted");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.fafsa.submitted)}</DataDisplay>
    ) : (
      <Select
        label={property?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.fafsa.submitted)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFafsa(
            produce(props.fafsa, (draft) => {
              draft.submitted = value?.id;
            })
          )
        }
      />
    );
  };

  const verifiedField = () => {
    const propertyName = pn<StudentFafsa>("verified");

    return props.crudMode === "view" ? (
      <DataDisplay label={verifiedDisplayField?.displayName}>{formatBoolean(props.fafsa.verified)}</DataDisplay>
    ) : (
      <Select
        label={verifiedDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.fafsa.verified)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFafsa(
            produce(props.fafsa, (draft) => {
              draft.verified = value?.id;
            })
          )
        }
      />
    );
  };

  const completedField = () => {
    const propertyName = pn<StudentFafsa>("completed");

    return props.crudMode === "view" ? (
      <DataDisplay label={completedDisplayField?.displayName}>{formatBoolean(props.fafsa.completed)}</DataDisplay>
    ) : (
      <Select
        label={completedDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.fafsa.completed)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFafsa(
            produce(props.fafsa, (draft) => {
              draft.completed = value?.id;
            })
          )
        }
      />
    );
  };

  const notApplicableField = () => {
    const propertyName = pn<StudentFafsa>("notApplicable");

    return props.crudMode === "view" ? (
      <DataDisplay label={notApplicableDisplayField?.displayName}>
        {props.fafsa.notApplicable === true && formatBoolean(props.fafsa.notApplicable)}
      </DataDisplay>
    ) : (
      <Select
        label={notApplicableDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.fafsa.notApplicable)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFafsa(
            produce(props.fafsa, (draft) => {
              draft.notApplicable = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {yearField()}
      </Grid>

      {dateDisplayField && (
        <Grid item xs={smallFieldBreakpoints}>
          {dateField()}
        </Grid>
      )}

      {amountDisplayField && (
        <Grid item xs={smallFieldBreakpoints}>
          {amountField()}
        </Grid>
      )}

      <GridBreak />

      {/* <Grid item xs={smallFieldBreakpoints}>
        {submittedField()}
      </Grid> */}
      <Grid item xs={xSmallFieldBreakpoints}>
        <YesNoSwitch
          label="Submitted"
          value={props.fafsa.submitted}
          onChange={(value) =>
            props.setFafsa(
              produce(props.fafsa, (draft) => {
                draft.submitted = value;
              })
            )
          }
        />
      </Grid>

      {completedDisplayField && (
        <Grid item xs={xSmallFieldBreakpoints}>
          <YesNoSwitch
            label={completedDisplayField.displayName}
            value={props.fafsa.completed}
            onChange={(value) =>
              props.setFafsa(
                produce(props.fafsa, (draft) => {
                  draft.completed = value;
                })
              )
            }
          />
        </Grid>
      )}

      {verifiedDisplayField && (
        <Grid item xs={xSmallFieldBreakpoints}>
          <YesNoSwitch
            label={verifiedDisplayField.displayName}
            value={props.fafsa.verified}
            onChange={(value) =>
              props.setFafsa(
                produce(props.fafsa, (draft) => {
                  draft.verified = value;
                })
              )
            }
          />
        </Grid>
      )}

      {notApplicableDisplayField && (
        <Grid item xs={xSmallFieldBreakpoints}>
          <YesNoSwitch
            label={notApplicableDisplayField.displayName}
            value={props.fafsa.notApplicable}
            onChange={(value) =>
              props.setFafsa(
                produce(props.fafsa, (draft) => {
                  draft.notApplicable = value;
                })
              )
            }
          />
        </Grid>
      )}
    </Grid>
  );
};
